import { HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { map } from 'rxjs';
import { CHECKING_ACCOUNTS_LIST_URL } from './savings-account-nickname.interceptor';

/**
 * Value of `bank_id` that identifies a CAIXA bank account.
 */
export const CAIXA_BANK_ID = '104';

/**
 * Checking account fields relevant to this interceptor.
 */
interface CheckingAccountLike {
  bank_id?: string | number | null;
  last_statement_balance?: number | string | null;
  [key: string]: unknown;
}

/**
 * Checking accounts list response body shape.
 */
interface CheckingAccountsListBody {
  results?: CheckingAccountLike[];
  [key: string]: unknown;
}

/**
 * Whether the account belongs to CAIXA (bank_id 104).
 */
function isCaixaAccount(account: CheckingAccountLike): boolean {
  return String(account?.bank_id) === CAIXA_BANK_ID;
}

/**
 * Parses the account balance used for ordering. Missing or invalid balances
 * resolve to -Infinity so those accounts sink to the end of the list.
 */
function balanceOf(account: CheckingAccountLike): number {
  const balance = Number(account?.last_statement_balance);

  return account?.last_statement_balance != null && !Number.isNaN(balance) ? balance : -Infinity;
}

/**
 * Reorders the checking accounts list so CAIXA accounts come first.
 */
function orderAccounts(accounts: CheckingAccountLike[]): CheckingAccountLike[] {
  const caixaAccounts = accounts.filter((account) => isCaixaAccount(account));
  const otherAccounts = accounts
    .filter((account) => !isCaixaAccount(account))
    .sort((a, b) => balanceOf(b) - balanceOf(a));

  return [...caixaAccounts, ...otherAccounts];
}

/**
 * CAIXA custom layer: the bank accounts carousel must push the company's
 * CAIXA accounts (bank_id 104) to the left so they are seen first, keeping
 * their original relative order. The remaining (OPF) accounts follow, ordered
 * by highest `last_statement_balance`; accounts without a valid balance go
 * last. Applies to the checking accounts list endpoint that feeds both the
 * carousel and the accounts modal.
 */
export const checkingAccountsOrderInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.includes(CHECKING_ACCOUNTS_LIST_URL)) {
    return next(req);
  }

  return next(req).pipe(
    map((event: HttpEvent<unknown>) => {
      if (!(event instanceof HttpResponse)) {
        return event;
      }

      const body = event.body as CheckingAccountsListBody | null;

      if (!Array.isArray(body?.results)) {
        return event;
      }

      return event.clone({
        body: {
          ...body,
          results: orderAccounts(body.results),
        },
      });
    }),
  );
};
